// src/groupCodes/redeemCode.js
// Group code validation and redemption functions

import { ref, get, set, runTransaction } from "firebase/database";
import { database } from "../firebaseConfig";

// Tour ID to user-friendly name mapping
const TOUR_NAMES = {
  'san-juan': 'San Juan Tour',
  'mexico-city-scavenger-hunt-tour': 'Mexico City Scavenger Hunt Tour'
};

/**
 * Validate a group code or gift code
 * @param {string} code - The group/gift code to validate
 * @param {string} userId - The user's Firebase UID
 * @returns {Promise<{valid: boolean, error?: string, tourId?: string, tourName?: string, isGiftCode?: boolean}>}
 */
export async function validateGroupCode(code, userId) {
  try {
    if (!code || !code.trim()) {
      return { valid: false, error: "Please enter a code" };
    }

    // For gift codes, preserve hyphens but uppercase and remove spaces
    // For group codes, remove hyphens too
    const upperCode = code.toUpperCase().replace(/\s/g, '');

    // Try gift codes first (with hyphens preserved)
    let codeRef = ref(database, `giftCodes/${upperCode}`);
    let snapshot = await get(codeRef);
    let isGiftCode = true;
    let normalizedCode = upperCode;

    // If not found in gift codes, try group codes (remove hyphens)
    if (!snapshot.exists()) {
      normalizedCode = upperCode.replace(/-/g, '');
      codeRef = ref(database, `groupCodes/${normalizedCode}`);
      snapshot = await get(codeRef);
      isGiftCode = false;
    }

    if (!snapshot.exists()) {
      return { valid: false, error: "Invalid code" };
    }

    const codeData = snapshot.val();

    // Check if code is inactive (gift codes have status field)
    if (isGiftCode && codeData.status !== 'active') {
      return { valid: false, error: "This code is no longer active" };
    }

    // Check if expired (if expiration is set)
    if (codeData.expiresAt && Date.now() > codeData.expiresAt) {
      return { valid: false, error: "This code has expired" };
    }

    // Check if at max uses
    const currentUses = codeData.currentUses || 0;
    if (currentUses >= codeData.maxUses) {
      return { valid: false, error: "This code has reached its maximum uses" };
    }

    // Check if user already redeemed this code
    if (codeData.usedBy && codeData.usedBy[userId]) {
      return { valid: false, error: "You've already redeemed this code" };
    }

    return {
      valid: true,
      tourId: codeData.tourId,
      tourName: codeData.tourName || TOUR_NAMES[codeData.tourId] || codeData.tourId,
      isGiftCode: isGiftCode,
      normalizedCode: normalizedCode
    };
  } catch (error) {
    console.error("Error validating code:", error);
    return { valid: false, error: "Failed to validate code. Please try again." };
  }
}

/**
 * Redeem a group code or gift code for a user
 * @param {string} userId - The user's Firebase UID
 * @param {string} userEmail - The user's email address
 * @param {string} code - The group/gift code to redeem
 * @returns {Promise<{success: boolean, error?: string, tourId?: string, tourName?: string}>}
 */
export async function redeemGroupCode(userId, userEmail, code) {
  try {
    // Validate first
    const validation = await validateGroupCode(code, userId);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    const { tourId, tourName, isGiftCode, normalizedCode } = validation;

    const codeType = isGiftCode ? 'gift-code' : 'group-code';
    const dbPath = isGiftCode ? 'giftCodes' : 'groupCodes';

    console.log(`[${codeType}] Redeeming code ${normalizedCode} for user ${userId}`);

    // Add user to code's usedBy list
    const usedByRef = ref(database, `${dbPath}/${normalizedCode}/usedBy/${userId}`);
    await set(usedByRef, {
      redeemedAt: Date.now(),
      email: userEmail
    });

    // Increment usage count
    const currentUsesRef = ref(database, `${dbPath}/${normalizedCode}/currentUses`);
    await runTransaction(currentUsesRef, (currentUses) => {
      return (currentUses || 0) + 1;
    });

    // Add to user's redeemed codes
    const userCodeRef = ref(database, `users/${userId}/redeemedCodes/${normalizedCode}`);
    await set(userCodeRef, {
      tourId: tourId,
      redeemedAt: Date.now(),
      type: codeType
    });

    // Add to validBuyers for immediate access (works with existing access control)
    const safeEmailKey = userEmail.toLowerCase().replace(/\./g, ',');
    const validBuyerRef = ref(database, `validBuyers/${safeEmailKey}/${tourId}`);
    await set(validBuyerRef, {
      granted: true,
      grantedAt: Date.now(),
      source: codeType,
      code: normalizedCode
    });

    console.log(`[${codeType}] ✅ Code redeemed successfully for ${tourId}`);

    return {
      success: true,
      tourId: tourId,
      tourName: tourName
    };
  } catch (error) {
    console.error("Error redeeming code:", error);
    return { success: false, error: "Failed to redeem code. Please try again." };
  }
}

/**
 * Get user's redeemed codes
 * @param {string} userId - The user's Firebase UID
 * @returns {Promise<Object>} Object with redeemed codes
 */
export async function getUserRedeemedCodes(userId) {
  try {
    const codesRef = ref(database, `users/${userId}/redeemedCodes`);
    const snapshot = await get(codesRef);

    if (snapshot.exists()) {
      return snapshot.val();
    }
    return {};
  } catch (error) {
    console.error("Error getting user's redeemed codes:", error);
    return {};
  }
}
